import {
  LCD_CONNECTION_I2C,
  LCD_CONNECTION_PARALLEL,
  getLcdI2cUi,
} from '@skyrover/blockly-blocks';
import type { Block } from 'blockly/core';

import { arduinoGenerator, blockTraceComment, Order } from './arduino-generator.js';
import { beginnerDisplayState, type LcdConfig } from './beginner-display-state.js';

const LCD_NAME = 'skyroverLcd';
const DEFAULT_I2C_ADDRESS = '0x27';

function statementPrefix(block: Block, code: string): string {
  return `${blockTraceComment(block)}\n${code}`;
}

function pinCode(pin: string): string {
  const trimmed = pin.trim();
  if (/^A\d+$/i.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  return trimmed;
}

function isEspBoard(): boolean {
  const id = arduinoGenerator.activeBoard?.id?.toLowerCase() ?? '';
  return id.includes('esp32') || id.includes('esp8266');
}

function defaultLcdConfig(): LcdConfig {
  const i2c = getLcdI2cUi();
  if (i2c.mode === 'fixed') {
    return {
      connection: LCD_CONNECTION_I2C as LcdConfig['connection'],
      scl: i2c.scl,
      sda: i2c.sda,
      i2cAddress: DEFAULT_I2C_ADDRESS,
    };
  }
  return {
    connection: LCD_CONNECTION_I2C as LcdConfig['connection'],
    scl: '22',
    sda: '21',
    i2cAddress: DEFAULT_I2C_ADDRESS,
  };
}

function readLcdConfigFromBlock(block: Block): LcdConfig {
  const connection = String(block.getFieldValue('CONNECTION') ?? LCD_CONNECTION_PARALLEL);
  if (connection === LCD_CONNECTION_I2C) {
    const ui = getLcdI2cUi();
    const scl =
      ui.mode === 'fixed'
        ? ui.scl
        : String(block.getFieldValue('SCL_PIN') ?? '22');
    const sda =
      ui.mode === 'fixed'
        ? ui.sda
        : String(block.getFieldValue('SDA_PIN') ?? '21');
    return {
      connection: LCD_CONNECTION_I2C as LcdConfig['connection'],
      scl,
      sda,
      i2cAddress: DEFAULT_I2C_ADDRESS,
    };
  }

  return {
    connection: LCD_CONNECTION_PARALLEL as LcdConfig['connection'],
    rs: String(block.getFieldValue('RS_PIN') ?? '12'),
    en: String(block.getFieldValue('EN_PIN') ?? '11'),
    d4: String(block.getFieldValue('D4_PIN') ?? '5'),
    d5: String(block.getFieldValue('D5_PIN') ?? '4'),
    d6: String(block.getFieldValue('D6_PIN') ?? '3'),
    d7: String(block.getFieldValue('D7_PIN') ?? '2'),
    i2cAddress: DEFAULT_I2C_ADDRESS,
  };
}

function ensureLcdSupport(config: LcdConfig = beginnerDisplayState.lcd ?? defaultLcdConfig()): void {
  beginnerDisplayState.lcd = config;

  if (config.connection === LCD_CONNECTION_I2C) {
    arduinoGenerator.addInclude('#include <Wire.h>');
    arduinoGenerator.addInclude('#include <LiquidCrystal_I2C.h>');
    if (!beginnerDisplayState.lcdDeclared) {
      arduinoGenerator.addGlobal(
        `LiquidCrystal_I2C ${LCD_NAME}(${config.i2cAddress}, 16, 2);`,
      );
      beginnerDisplayState.lcdDeclared = true;
    }
    if (isEspBoard() && config.sda && config.scl) {
      arduinoGenerator.addSetupOnce(
        'beginner-lcd-wire',
        `Wire.begin(${pinCode(config.sda)}, ${pinCode(config.scl)});`,
      );
    } else {
      arduinoGenerator.addSetupOnce('beginner-lcd-wire', 'Wire.begin();');
    }
    arduinoGenerator.addSetupOnce('beginner-lcd-begin', `${LCD_NAME}.init();`);
    arduinoGenerator.addSetupOnce('beginner-lcd-backlight', `${LCD_NAME}.backlight();`);
    return;
  }

  arduinoGenerator.addInclude('#include <LiquidCrystal.h>');
  const rs = pinCode(config.rs ?? '12');
  const en = pinCode(config.en ?? '11');
  const d4 = pinCode(config.d4 ?? '5');
  const d5 = pinCode(config.d5 ?? '4');
  const d6 = pinCode(config.d6 ?? '3');
  const d7 = pinCode(config.d7 ?? '2');
  if (!beginnerDisplayState.lcdDeclared) {
    arduinoGenerator.addGlobal(
      `LiquidCrystal ${LCD_NAME}(${rs}, ${en}, ${d4}, ${d5}, ${d6}, ${d7});`,
    );
    beginnerDisplayState.lcdDeclared = true;
  }
  arduinoGenerator.addSetupOnce('beginner-lcd-begin', `${LCD_NAME}.begin(16, 2);`);
}

export function registerBeginnerLcdGenerators(): void {
  arduinoGenerator.forBlock['beginner_lcd_configure'] = (block: Block) => {
    const config = readLcdConfigFromBlock(block);
    ensureLcdSupport(config);
    return statementPrefix(block, '// LCD configured\n');
  };

  arduinoGenerator.forBlock['beginner_lcd_display_text'] = (block: Block) => {
    ensureLcdSupport();
    const text = arduinoGenerator.valueToCode(block, 'TEXT', Order.NONE) || '""';
    return statementPrefix(block, `${LCD_NAME}.print(${text});\n`);
  };

  arduinoGenerator.forBlock['beginner_lcd_clear'] = (block: Block) => {
    ensureLcdSupport();
    return statementPrefix(block, `${LCD_NAME}.clear();\n`);
  };

  arduinoGenerator.forBlock['beginner_lcd_set_cursor'] = (block: Block) => {
    ensureLcdSupport();
    const column = Math.max(0, Math.round(Number(block.getFieldValue('COLUMN') ?? 0)));
    const row = Math.max(0, Math.round(Number(block.getFieldValue('ROW') ?? 0)));
    return statementPrefix(block, `${LCD_NAME}.setCursor(${column}, ${row});\n`);
  };

  arduinoGenerator.forBlock['beginner_lcd_scroll_left'] = (block: Block) => {
    ensureLcdSupport();
    return statementPrefix(block, `${LCD_NAME}.scrollDisplayLeft();\n`);
  };

  arduinoGenerator.forBlock['beginner_lcd_scroll_right'] = (block: Block) => {
    ensureLcdSupport();
    return statementPrefix(block, `${LCD_NAME}.scrollDisplayRight();\n`);
  };
}

registerBeginnerLcdGenerators();
